// The competition clock as two phases with the real interval between them. Pure: `now` is
// passed in, so the server and the browser can render the same bar from the same instant.
import type { Timeframe } from "@/lib/api/types";
import { clamp } from "@/lib/utils";

export interface PhaseView {
  start: string;
  end: string;
  /** How much of the phase has elapsed, 0 to 1. */
  fill: number;
  done: boolean;
  running: boolean;
}

/** Where the clock actually is, which is not the same as the view's mode. */
export type Phase = "scheduled" | "upload" | "gap" | "evaluation" | "closed";

/** Share of the whole window each segment takes, summing to 1. */
export interface Weights {
  upload: number;
  gap: number;
  evaluation: number;
}

/**
 * What the clock is counting to. A competition that has not opened yet is still on a
 * schedule, so it counts down to the moment it opens - the old dashboard did this and
 * it is the number a miner actually wants ("how long until I can upload").
 */
export interface Countdown {
  kind: "starts" | "ends";
  phase: "upload" | "evaluation";
  at: string;
}

export interface TimelineView {
  upload: PhaseView;
  /**
   * The wait between upload closing and evaluation opening, or null when the two are
   * contiguous. Most competitions hand over instantly, and a bar that always shows a gap
   * invents dead time the schedule does not have.
   */
  gap: PhaseView | null;
  evaluation: PhaseView;
  weights: Weights;
  phase: Phase;
  /** Null only once evaluation has closed: there is nothing left to wait for. */
  countdown: Countdown | null;
}

function phase(start: string, end: string, now: number): PhaseView {
  const from = Date.parse(start);
  const to = Date.parse(end);
  const span = to - from;
  // A zero or backwards span is a backend mistake, not a phase in progress: show it as
  // finished rather than dividing by zero and painting a full bar at random.
  const fill = Number.isFinite(span) && span > 0 ? clamp((now - from) / span, 0, 1) : now >= to ? 1 : 0;
  return { start, end, fill, done: now >= to, running: now >= from && now < to };
}

/**
 * Segment widths in proportion to real duration. Equal halves are the fallback for a
 * timeframe we cannot measure - better a neutral bar than one scaled by a NaN.
 */
function weigh(uploadMs: number, gapMs: number, evaluationMs: number): Weights {
  const total = uploadMs + gapMs + evaluationMs;
  if (!Number.isFinite(total) || total <= 0) return { upload: 0.5, gap: 0, evaluation: 0.5 };
  return { upload: uploadMs / total, gap: gapMs / total, evaluation: evaluationMs / total };
}

/** The flex share of one segment. A real but tiny phase still gets a sliver. */
export function segmentFlex(weight: number): string {
  return `${Math.max(weight, 0.01)} 1 0%`;
}

/**
 * The labels under the bar, one per segment and as wide as it, so each sits under its own
 * stretch of time. Upload hugs the left edge and evaluation the right, which leaves the
 * middle to the gap - centred on the gap itself, not on the whole bar.
 */
export interface TimelineColumn {
  key: "upload" | "gap" | "evaluation";
  flex: string;
  align: "start" | "center" | "end";
  state: "done" | "running" | "upcoming";
  start: string;
  end: string;
}

function stateOf(p: PhaseView): TimelineColumn["state"] {
  return p.running ? "running" : p.done ? "done" : "upcoming";
}

export function timelineColumns(view: TimelineView): TimelineColumn[] {
  const column = (key: TimelineColumn["key"], p: PhaseView, weight: number, align: TimelineColumn["align"]) => ({
    key,
    flex: segmentFlex(weight),
    align,
    state: stateOf(p),
    start: p.start,
    end: p.end,
  });
  return [
    column("upload", view.upload, view.weights.upload, "start"),
    ...(view.gap ? [column("gap", view.gap, view.weights.gap, "center")] : []),
    column("evaluation", view.evaluation, view.weights.evaluation, "end"),
  ];
}

/**
 * What the badge beside the competition name says. It names the phase the clock is in,
 * not the view: the default view is the newest competition even when it closed last
 * week, and "Evaluation active" there would be a plain lie. An archive or a closing view
 * says so instead. `live` marks the two phases in which something is actually happening.
 */
export type TimelineStatus = { key: Phase | "archive" | "closing"; live: boolean };

export function timelineStatus(mode: "current" | "closing" | "archive", phase: Phase | null): TimelineStatus {
  if (mode === "archive") return { key: "archive", live: false };
  if (mode === "closing") return { key: "closing", live: false };
  const key = phase ?? "closed";
  return { key, live: key === "upload" || key === "evaluation" };
}

export function timelineView(timeframe: Timeframe, now: number): TimelineView {
  const upload = phase(timeframe.upload_start, timeframe.upload_end, now);
  const evaluation = phase(timeframe.evaluation_start, timeframe.evaluation_end, now);

  const uploadEnd = Date.parse(timeframe.upload_end);
  const evaluationStart = Date.parse(timeframe.evaluation_start);
  const gapMs = evaluationStart - uploadEnd;
  const gap = Number.isFinite(gapMs) && gapMs > 0 ? phase(timeframe.upload_end, timeframe.evaluation_start, now) : null;

  const current: Phase = upload.running
    ? "upload"
    : evaluation.running
      ? "evaluation"
      : evaluation.done
        ? "closed"
        : upload.done
          ? "gap"
          : "scheduled";

  const countdown: Countdown | null =
    current === "upload"
      ? { kind: "ends", phase: "upload", at: timeframe.upload_end }
      : current === "evaluation"
        ? { kind: "ends", phase: "evaluation", at: timeframe.evaluation_end }
        : current === "scheduled"
          ? { kind: "starts", phase: "upload", at: timeframe.upload_start }
          : current === "gap"
            ? { kind: "starts", phase: "evaluation", at: timeframe.evaluation_start }
            : null;

  return {
    upload,
    gap,
    evaluation,
    weights: weigh(
      Date.parse(timeframe.upload_end) - Date.parse(timeframe.upload_start),
      gap ? gapMs : 0,
      Date.parse(timeframe.evaluation_end) - evaluationStart,
    ),
    phase: current,
    countdown,
  };
}
